import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Bell, BellOff, Loader2, Mail, Settings } from "lucide-react";
import { apiGet, apiPatch } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const UI = {
  it: {
    eyebrow: "Account · Impostazioni",
    title: "Preferenze",
    desc: "Gestisci come ricevi le comunicazioni della piattaforma.",
    srs_title: "Promemoria SRS",
    srs_desc: "Ricevi una notifica via email quando hai carte di Ripetizione Spaziata da ripassare. Inviata al massimo una volta al giorno.",
    srs_on: "Attivi",
    srs_off: "Disattivati",
    last_sent: "Ultimo invio",
    never_sent: "Nessun invio ancora",
    save_error: "Impossibile salvare. Riprova.",
    save_success: "Preferenze salvate.",
    only_paid: "I promemoria SRS sono disponibili solo per gli iscritti Premium, Premium Plus o Academia.",
    loading: "Caricamento…",
  },
  es: {
    eyebrow: "Cuenta · Configuración",
    title: "Preferencias",
    desc: "Gestiona cómo recibís las comunicaciones de la plataforma.",
    srs_title: "Recordatorios SRS",
    srs_desc: "Recibí una notificación por email cuando tengas tarjetas de Repetición Espaciada para repasar. Como máximo una vez al día.",
    srs_on: "Activos",
    srs_off: "Desactivados",
    last_sent: "Último envío",
    never_sent: "Ningún envío todavía",
    save_error: "No se pudo guardar. Reintentá.",
    save_success: "Preferencias guardadas.",
    only_paid: "Los recordatorios SRS están disponibles sólo para suscriptores Premium, Premium Plus o Academia.",
    loading: "Cargando…",
  },
};

function fmtDate(iso, lang) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(lang === "es" ? "es-ES" : "it-IT", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(iso).slice(0, 16); }
}

export default function AccountPreferences() {
  const { user } = useAuth();
  const lang = user?.preferred_language === "es" ? "es" : "it";
  const t = UI[lang];
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  // A user is "paid" if they have any of the active subscription flags.
  // We don't render the SRS toggle for free users (matches backend gate).
  const isPaid = (
    user?.subscription_level === "premium"
    || user?.subscription_level === "premium_plus"
    || user?.has_academy_access
    || user?.academy_lifetime
    || user?.is_vip
  );

  useEffect(() => {
    apiGet("/me/notifications").then(setPrefs).catch(() => setPrefs({}));
  }, []);

  const toggleSrs = async () => {
    if (!prefs) return;
    setBusy(true);
    const next = !(prefs.srs_reminder_enabled !== false);
    try {
      const res = await apiPatch("/me/notifications", { srs_reminder_enabled: next });
      setPrefs({ ...prefs, srs_reminder_enabled: res.srs_reminder_enabled });
      toast.success(t.save_success);
    } catch {
      toast.error(t.save_error);
    } finally {
      setBusy(false);
    }
  };

  if (!prefs) {
    return (
      <div className="px-6 py-12 grid place-items-center"
           data-testid="account-prefs-loading">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#7B5CE0" }} />
      </div>
    );
  }

  const enabled = prefs.srs_reminder_enabled !== false;
  const lastSent = fmtDate(prefs.srs_reminder_last_sent_at, lang);

  return (
    <div data-testid="account-preferences-page" className="px-4 sm:px-8 py-10">
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className="max-w-2xl mx-auto">
        <p className="text-[11px] uppercase tracking-[0.22em] text-brand-secondary mb-2">
          {t.eyebrow}
        </p>
        <div className="flex items-center gap-2 mb-2">
          <Settings className="h-5 w-5" style={{ color: "#7B5CE0" }} />
          <h1 className="font-serif text-3xl text-brand-primary">{t.title}</h1>
        </div>
        <p className="text-sm text-brand-secondary mb-8">{t.desc}</p>

        {/* SRS reminders card */}
        <div data-testid="srs-prefs-card"
             className="rounded-2xl bg-white shadow-premium border border-brand-border p-6">
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-xl grid place-items-center shrink-0"
                 style={{ background: "rgba(123,92,224,0.12)" }}>
              {enabled ? (
                <Bell className="h-5 w-5" style={{ color: "#7B5CE0" }} />
              ) : (
                <BellOff className="h-5 w-5" style={{ color: "#7B5CE0", opacity: 0.6 }} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-brand-primary mb-1">
                {t.srs_title}
              </h2>
              <p className="text-sm text-brand-secondary leading-relaxed mb-4">
                {t.srs_desc}
              </p>
              {isPaid ? (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    <button
                      data-testid="srs-prefs-toggle"
                      onClick={toggleSrs}
                      disabled={busy}
                      role="switch"
                      aria-checked={enabled}
                      className="relative inline-flex items-center"
                    >
                      <span
                        className="w-11 h-6 rounded-full transition-colors"
                        style={{
                          background: enabled ? "#7B5CE0" : "#D7D2E8",
                          opacity: busy ? 0.7 : 1,
                        }}
                      >
                        <span
                          className="block h-5 w-5 rounded-full bg-white shadow transition-transform mt-0.5 ml-0.5"
                          style={{ transform: enabled ? "translateX(20px)" : "translateX(0)" }}
                        />
                      </span>
                    </button>
                    <span className="text-sm font-medium"
                          data-testid="srs-prefs-state"
                          style={{ color: enabled ? "#5A3FC0" : "#9AA197" }}>
                      {enabled ? t.srs_on : t.srs_off}
                    </span>
                  </div>
                  <div className="text-xs flex items-center gap-1.5"
                       style={{ color: "#7A8475" }}>
                    <Mail className="h-3.5 w-3.5" />
                    <span>{t.last_sent}: {lastSent || t.never_sent}</span>
                  </div>
                </>
              ) : (
                <div className="rounded-xl p-3 text-xs"
                     style={{ background: "#FFF7E0", border: "1px solid #F0DC9A",
                              color: "#6B4F00" }}
                     data-testid="srs-prefs-not-eligible">
                  {t.only_paid}
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
